<script setup lang="ts">
</script>

<template>
  <div class="my-24 sm:my-32 xl:my-48">
    <div class="mx-auto max-w-2xl px-6 lg:max-w-7xl lg:px-8">
      <h2 class="text-center text-base/7 font-semibold text-primary-600">
        {{ $t('bento.sectionLabel') }}
      </h2>
      <p class="mx-auto mt-2 max-w-lg text-balance text-center font-serif text-4xl font-semibold text-gray-900 sm:text-5xl">
        {{ $t('bento.title') }}
      </p>
      <div class="mt-10 grid gap-4 sm:mt-16 lg:grid-cols-3 lg:grid-rows-2">
        <div class="relative lg:row-span-2">
          <div class="absolute inset-px rounded-lg bg-white lg:rounded-l-[2rem]" />
          <div class="relative flex h-full flex-col overflow-hidden rounded-[calc(theme(borderRadius.lg)+1px)] lg:rounded-l-[calc(2rem+1px)]">
            <div class="px-8 pb-3 pt-8 sm:px-10 sm:pb-0 sm:pt-10">
              <p class="mt-2 text-lg/7 font-medium tracking-tight text-gray-950 max-lg:text-center">
                {{ $t('bento.mobile.title') }}
              </p>
              <p class="mt-2 max-w-lg text-sm/6 text-gray-600 max-lg:text-center">
                {{ $t('bento.mobile.description') }}
              </p>
            </div>
            <div class="relative min-h-80 w-full grow [container-type:inline-size] max-lg:mx-auto max-lg:max-w-sm sm:min-h-[30rem] lg:min-h-0 xl:min-h-[30rem]">
              <div class="absolute inset-x-10 bottom-0 top-10 overflow-hidden rounded-t-[12cqw] border-x-[3cqw] border-t-[3cqw] border-gray-700 bg-gray-900 shadow-2xl">
                <NuxtImg
                  loading="lazy"
                  class="pointer-events-none size-full select-none object-cover object-top"
                  :alt="$t('bento.mobile.alt')"
                  width="293"
                  src="/assets/screenshot-mobile-2.png"
                />
              </div>
            </div>
          </div>
          <div class="pointer-events-none absolute inset-px select-none rounded-lg shadow ring-1 ring-black/5 lg:rounded-l-[2rem]" />
        </div>
        <div class="relative max-lg:row-start-1">
          <div class="absolute inset-px rounded-lg bg-white max-lg:rounded-t-[2rem]" />
          <div class="relative flex h-full flex-col overflow-hidden rounded-[calc(theme(borderRadius.lg)+1px)] max-lg:rounded-t-[calc(2rem+1px)]">
            <div class="px-8 pt-8 sm:px-10 sm:pt-10">
              <p class="mt-2 text-lg/7 font-medium tracking-tight text-gray-950 max-lg:text-center">
                {{ $t('bento.voice.title') }}
              </p>
              <p class="mt-2 max-w-lg text-sm/6 text-gray-600 max-lg:text-center">
                {{ $t('bento.voice.description') }}
              </p>
            </div>
            <div class="flex size-full items-center justify-center gap-5 py-10 sm:py-5">
              <div class="relative z-0 flex animate-record-pulse items-center justify-center rounded-full p-5 sm:p-6">
                <Icon
                  name="heroicons:microphone-solid"
                  class="size-8 text-red-600 sm:size-10"
                />
              </div>
            </div>
          </div>
          <div class="pointer-events-none absolute inset-px select-none rounded-lg shadow ring-1 ring-black/5 max-lg:rounded-t-[2rem]" />
        </div>
        <div class="relative max-lg:row-start-3 lg:col-start-2 lg:row-start-2">
          <div class="absolute inset-px rounded-lg bg-white" />
          <div class="relative flex h-full flex-col overflow-hidden rounded-[calc(theme(borderRadius.lg)+1px)]">
            <div class="px-8 pt-8 sm:px-10 sm:pt-10">
              <p class="mt-2 text-lg/7 font-medium tracking-tight text-gray-950 max-lg:text-center">
                {{ $t('bento.suggestions.title') }}
              </p>
              <p class="mt-2 max-w-lg text-sm/6 text-gray-600 max-lg:text-center">
                {{ $t('bento.suggestions.description') }}
              </p>
            </div>
            <div class="flex size-full items-end justify-center gap-5 px-8 pt-8 sm:px-10 sm:pt-10">
              <NuxtImg
                loading="lazy"
                class="pointer-events-none mx-auto -mb-1 h-32 w-full max-w-[315px] select-none object-cover object-top"
                width="315"
                height="200"
                :alt="$t('bento.suggestions.alt')"
                fit="cover"
                :modifiers="{
                  pos: 'top',
                  gravity: '0.5x0',
                }"
                src="/assets/suggestions.png"
              />
            </div>
          </div>
          <div class="pointer-events-none absolute inset-px select-none rounded-lg shadow ring-1 ring-black/5" />
        </div>
        <div class="relative lg:row-span-2">
          <div class="absolute inset-px rounded-lg bg-white max-lg:rounded-b-[2rem] lg:rounded-r-[2rem]" />
          <div class="relative flex h-full flex-col overflow-hidden rounded-[calc(theme(borderRadius.lg)+1px)] max-lg:rounded-b-[calc(2rem+1px)] lg:rounded-r-[calc(2rem+1px)]">
            <div class="px-8 pb-3 pt-8 sm:px-10 sm:pb-0 sm:pt-10">
              <p class="mt-2 text-lg/7 font-medium tracking-tight text-gray-950 max-lg:text-center">
                {{ $t('bento.chat.title') }}
              </p>
              <p class="mt-2 max-w-lg text-sm/6 text-gray-600 max-lg:text-center">
                {{ $t('bento.chat.description') }}
              </p>
            </div>
            <div class="relative min-h-80 w-full grow [container-type:inline-size] max-lg:mx-auto max-lg:max-w-sm sm:min-h-[30rem] lg:min-h-0 xl:min-h-[30rem]">
              <div class="absolute inset-x-10 bottom-0 top-10 overflow-hidden rounded-t-3xl border border-b-0 border-blue-600 shadow-md">
                <NuxtImg
                  loading="lazy"
                  class="pointer-events-none size-full select-none object-cover object-top"
                  :alt="$t('bento.chat.alt')"
                  width="293"
                  src="/assets/chat.png"
                />
              </div>
            </div>
          </div>
          <div class="pointer-events-none absolute inset-px select-none rounded-lg shadow ring-1 ring-black/5 max-lg:rounded-b-[2rem] lg:rounded-r-[2rem]" />
        </div>
      </div>
    </div>
  </div>
</template>
